import { JSX } from 'react';
import { useInput } from 'react-admin';
import { Alert, Box, Checkbox, FormControlLabel, LinearProgress, Typography } from '@mui/material';
import { AclResourceNode } from '../../../../types/entities';
import { useAclTree } from './use-acl-tree';
import { applyToggle, descendantIds, isVerb } from './selection';

/**
 * The role screen's checkbox tree, bound to the form's `resources` field.
 *
 * Hand-rolled rather than pulled from a library. `@mui/x-tree-view` is not a dependency and this
 * needs two behaviours no generic tree gives for free. Ticking a parent ticks everything beneath
 * it, because that is what granting a parent means server side; a tree that only recorded the
 * clicked node would show a role as narrower than it is. And a section's **verbs** draw on the
 * section's own row rather than as four rows of their own, because a role screen with four
 * hundred rows is one nobody can read.
 *
 * Verbs are marked by the server - `AclResourceProvider` generates them from a section's `verbs`
 * key and puts the verb name on the node - so nothing here parses an id to work out what it is.
 *
 * Disabled boxes are resources the signed-in admin cannot use themselves. HQ-API refuses those
 * regardless; showing them greyed is so the reason is visible before saving rather than after.
 */

type AclResourceTreeInputProps = {
    source: string,
    /** Ignored while the role grants everything, which is what the All toggle above sets. */
    disabled?: boolean,
}

type NodeProps = {
    node: AclResourceNode,
    depth: number,
    /** Ids of the nodes above this one, root first. Unticking has to clear them too. */
    ancestors: string[],
    selected: string[],
    grantable: string[],
    disabled: boolean,
    onToggle: (node: AclResourceNode, ancestors: string[], checked: boolean) => void,
}

type VerbProps = {
    verb: AclResourceNode,
    ancestors: string[],
    selected: string[],
    grantable: string[],
    disabled: boolean,
    onToggle: (node: AclResourceNode, ancestors: string[], checked: boolean) => void,
}

/**
 * One verb checkbox, sitting on its section's row.
 *
 * Labelled with the verb alone - View, Create, Edit, Delete - because the section it belongs to is
 * the text immediately to its left. Spelling out the full id here, as the section rows do, would
 * make the row unreadable at four verbs across.
 */
const AclVerb: (props: VerbProps) => JSX.Element =
    ({ verb, ancestors, selected, grantable, disabled, onToggle }: VerbProps): JSX.Element => (
        <FormControlLabel
            sx={ { mr: 0 } }
            control={
                <Checkbox
                    size="small"
                    sx={ { py: 0.25 } }
                    checked={ selected.includes(verb.id) }
                    disabled={ disabled || !grantable.includes(verb.id) }
                    inputProps={ { 'aria-label': verb.id } }
                    onChange={ (event): void => onToggle(verb, ancestors, event.target.checked) }
                />
            }
            label={ <Typography component="span" variant="caption">{ verb.title }</Typography> }
        />
    );

const AclNode: (props: NodeProps) => JSX.Element =
    ({ node, depth, ancestors, selected, grantable, disabled, onToggle }: NodeProps): JSX.Element => {
        const children: AclResourceNode[] = node.children ?? [];
        // Verbs draw on this row, sections draw beneath it. The server has already put them in
        // order, verbs first, so no sorting happens here.
        const verbs: AclResourceNode[] = children.filter(isVerb);
        const sections: AclResourceNode[] = children.filter(
            (child: AclResourceNode): boolean => !isVerb(child)
        );

        const checked: boolean = selected.includes(node.id);
        // A parent whose descendants are partly ticked. Purely a display state; the value of a
        // parent box is only ever what is actually granted.
        const someBeneath: boolean = !checked
            && descendantIds(node).some((id: string): boolean => id !== node.id && selected.includes(id));
        const blocked: boolean = disabled || !grantable.includes(node.id);
        const beneath: string[] = [...ancestors, node.id];

        return (
            <Box sx={ { pl: depth === 0 ? 0 : 3 } }>
                <Box sx={ { display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2 } }>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={ checked }
                                indeterminate={ someBeneath }
                                disabled={ blocked }
                                inputProps={ { 'aria-label': node.id } }
                                onChange={ (event): void => onToggle(node, ancestors, event.target.checked) }
                            />
                        }
                        label={
                            <Box component="span">
                                <Typography component="span" variant="body2">{ node.title }</Typography>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={ { ml: 1, color: 'text.secondary' } }
                                >
                                    { node.id }
                                </Typography>
                            </Box>
                        }
                    />
                    { verbs.length > 0 &&
                        <Box sx={ { display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 } }>
                            { verbs.map((verb: AclResourceNode): JSX.Element => (
                                <AclVerb
                                    key={ verb.id }
                                    verb={ verb }
                                    ancestors={ beneath }
                                    selected={ selected }
                                    grantable={ grantable }
                                    disabled={ disabled }
                                    onToggle={ onToggle }
                                />
                            )) }
                        </Box>
                    }
                </Box>
                { sections.map((child: AclResourceNode): JSX.Element => (
                    <AclNode
                        key={ child.id }
                        node={ child }
                        depth={ depth + 1 }
                        ancestors={ beneath }
                        selected={ selected }
                        grantable={ grantable }
                        disabled={ disabled }
                        onToggle={ onToggle }
                    />
                )) }
            </Box>
        );
    };

export const AclResourceTreeInput: (props: AclResourceTreeInputProps) => JSX.Element =
    ({ source, disabled = false }: AclResourceTreeInputProps): JSX.Element => {
        const { field } = useInput({ source });
        const { tree, grantable, loading, error } = useAclTree();

        const selected: string[] = Array.isArray(field.value) ? field.value : [];

        const onToggle = (node: AclResourceNode, ancestors: string[], checked: boolean): void => {
            field.onChange(applyToggle(selected, node, ancestors, checked, grantable));
        };

        if (loading) {
            return <LinearProgress sx={ { margin: '10px 0', width: '50%' } } />;
        }

        if (error !== null || tree === null) {
            return <Alert severity="error" sx={ { my: 1 } }>{ error ?? 'The resource tree is empty' }</Alert>;
        }

        return (
            <Box sx={ { border: 1, borderColor: 'divider', borderRadius: 1, p: 2, my: 1, width: '100%' } }>
                { disabled &&
                    <Alert severity="info" sx={ { mb: 2 } }>
                        This role has access to everything, including resources added later.
                        Switch it to Custom to choose individual resources.
                    </Alert>
                }
                <AclNode
                    node={ tree }
                    depth={ 0 }
                    ancestors={ [] }
                    selected={ selected }
                    grantable={ grantable }
                    disabled={ disabled }
                    onToggle={ onToggle }
                />
            </Box>
        );
    };
